/**
 * PasswordInput — a password field with a show/hide eye and, for NEW passwords, a one-line live check that uses the same
 * rules the server enforces (length, common passwords, your own name or email). The server also checks known data
 * breaches, which can only be answered on submit.
 */
import { useState } from "react";
import { Check, Eye, EyeOff } from "lucide-react";
import { PASSWORD_MIN, passwordProblem } from "@demandhub-cms/shared";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";

export function PasswordInput({
  value,
  onChange,
  autoComplete,
  id,
  label,
  check,
  who,
  autoFocus,
  required = true,
  bare,
}: {
  value: string;
  onChange: (v: string) => void;
  autoComplete: "current-password" | "new-password";
  id: string;
  label: string;
  /** Show the live "is this a good new password" line. */
  check?: boolean;
  who?: { email?: string | null; name?: string | null };
  autoFocus?: boolean;
  required?: boolean;
  /** The caller draws the label (e.g. with a "Forgot password?" link beside it). */
  bare?: boolean;
}) {
  const [shown, setShown] = useState(false);
  const problem = check && value ? passwordProblem(value, who) : null;
  const good = check && value.length > 0 && !problem;
  return (
    <div>
      {!bare && (
        <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-neutral-700">
          {label}
        </label>
      )}
      <div className="relative">
        <Input
          id={id}
          type={shown ? "text" : "password"}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="pr-10"
          spellCheck={false}
          autoCapitalize="none"
        />
        <button
          type="button"
          onClick={() => setShown((s) => !s)}
          aria-label={shown ? "Hide password" : "Show password"}
          aria-pressed={shown}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-neutral-400 hover:text-neutral-700 focus-visible:ring-2 focus-visible:ring-accent-500/50"
        >
          {shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
      {check && (
        <p aria-live="polite" className={cn("mt-1.5 flex items-center gap-1.5 text-xs", good ? "text-success-700" : problem ? "text-danger-700" : "text-neutral-500")}>
          {good && <Check className="size-3.5" aria-hidden />}
          {good ? "Looks good." : problem ?? `At least ${PASSWORD_MIN} characters. A few random words work well.`}
        </p>
      )}
    </div>
  );
}
